import { RPC_ENDPOINT } from "@solomon-apps/config";
import { DEFAULT_SALT, TOKEN_DECIMALS } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import type { PublicKey } from "@solomon-labs/publickey";
import { ConnectionManager } from "@solomon-labs/solana-connection";
import { SolomonStake } from "@solomon-labs/solomon-protocol";

/**
 * Runs the real start-unstake instruction without signing or sending it.
 * A false result means the exact share amount is not currently actionable.
 */
export async function simulateStartUnstake(
    owner: PublicKey,
    sharesAmount: BigDecimal,
    signal?: AbortSignal
): Promise<boolean> {
    const scaledShares = sharesAmount.multiply(BigDecimal.pow10(TOKEN_DECIMALS.sUSDv));
    if (scaledShares.lt(1)) return false;

    const blockhashResponse = await ConnectionManager.getRpcClient().getLatestBlockhash();
    if (!blockhashResponse) {
        throw new Error("Failed to get a recent blockhash for unstake simulation");
    }

    const transaction = await SolomonStake.createStartUnstakeTransaction(owner, DEFAULT_SALT, scaledShares.toBigint(), {
        feePayer: owner,
        recentBlockhash: blockhashResponse.blockhash
    });
    const response = await fetch(RPC_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            jsonrpc: "2.0",
            id: 1,
            method: "simulateTransaction",
            params: [
                transaction.serializeBase64(false),
                {
                    encoding: "base64",
                    sigVerify: false,
                    commitment: "processed"
                }
            ]
        }),
        signal
    });

    if (!response.ok) {
        throw new Error(`Unstake simulation RPC failed with HTTP ${response.status}`);
    }

    const payload: unknown = await response.json();
    if (!isRecord(payload)) {
        throw new Error("Invalid unstake simulation RPC response");
    }
    if (payload.error) throw new Error("Unstake simulation RPC returned an error");
    if (!isRecord(payload.result) || !isRecord(payload.result.value) || !("err" in payload.result.value)) {
        throw new Error("Invalid unstake simulation RPC response");
    }

    return payload.result.value.err === null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}
